import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import backendService from 'services/backend.service';
import { notifyError } from 'libs/utils/Toaster';
import { IReferralAgreementData } from './ReferralAgreementForm';
import '../Referral.css';
import {
  IReportResult,
  IEntityOption,
  getDefaultDates,
  brandColor,
  fmtCurrency,
  downloadCSV,
} from './ReferralReportingPage.helpers';

interface IReferralReportingPageProps {
  agreements: IReferralAgreementData[];
}

const SKEL_ROWS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

const ReferralReportingPage: React.FC<IReferralReportingPageProps> = ({ agreements }) => {
  const [result, setResult] = useState<IReportResult | null>(null);
  const [loading, setLoading] = useState(false);

  const [merchants, setMerchants] = useState<IEntityOption[]>([]);
  const [parents, setParents] = useState<IEntityOption[]>([]);
  const [grandparents, setGrandparents] = useState<IEntityOption[]>([]);

  const [filterApm, setFilterApm] = useState('');
  const [filterMerchant, setFilterMerchant] = useState('');
  const [filterParent, setFilterParent] = useState('');
  const [filterGp, setFilterGp] = useState('');
  const { from: defaultFrom, to: defaultTo } = getDefaultDates();
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const [mer, par, gp] = await Promise.all([
          backendService.getMerchants().catch(() => []),
          backendService.getParents().catch(() => []),
          backendService.getGrandparents().catch(() => []),
        ]);
        setMerchants((mer as any[]).map((m) => ({ id: m.id, name: m.name })));
        setParents((par as any[]).map((p) => ({ id: p.id, name: p.name })));
        setGrandparents((gp as any[]).map((g) => ({ id: g.id, name: g.name })));
      } catch {
        // non-fatal
      }
    };
    loadEntities();
  }, []);

  const fetchData = useCallback(async (overrides?: {
    apm?: string; merchant?: string; parent?: string; gp?: string;
    from?: string; to?: string;
  }) => {
    setLoading(true);
    try {
      const data = await backendService.getReferralReportingData({
        dateFrom: (overrides?.from ?? dateFrom) || undefined,
        dateTo: (overrides?.to ?? dateTo) || undefined,
        agreementId: (overrides?.apm ?? filterApm) || undefined,
        merchantId: (overrides?.merchant ?? filterMerchant) || undefined,
        parentId: (overrides?.parent ?? filterParent) || undefined,
        grandparentId: (overrides?.gp ?? filterGp) || undefined,
      });
      setResult(data);
    } catch {
      notifyError('Failed to load referral reporting data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterApm, filterMerchant, filterParent, filterGp]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = result?.rows ?? [];

  // displayCurrency comes from the backend: invoicingCurrency of the filtered agreement, or USD when no agreement filter.
  const displayCurrency = result?.displayCurrency ?? 'USD';

  const summary = useMemo(() => {
    if (rows.length === 0) return null;
    if (result == null) return null;
    return {
      totalVolume: result.totalVolume,
      estReferralFee: result.estReferralFee,
      uniqueMerchants: result.uniqueMerchants,
      rowCount: result.rowCount,
      avgBips: result.avgBips,
    };
  }, [rows, result]);

  return (
    <div className="ref-reporting-page">

      {/* Page title row — outside the filters card */}
      <div className="rr-page-header">
        <h2 className="section-title">Referral Reporting</h2>
        <button
          className="btn btn-primary ref-reporting-export-btn"
          disabled={rows.length === 0 || loading}
          onClick={() => downloadCSV(rows)}
          type="button"
        >
          ↓ Export CSV
          {rows.length > 0 && ` (${rows.length.toLocaleString()} rows)`}
        </button>
      </div>

      {/* Filters card */}
      <div className="ref-reporting-filters-card">
        <div className="ref-reporting-filters-label">Filters</div>
        <div className="ref-reporting-filters-row">

          <div className="ref-reporting-filter-group">
            <div className="ref-reporting-filter-lbl">Agreement</div>
            <select
              aria-label="Filter by agreement"
              className="ref-reporting-select"
              onChange={(e) => setFilterApm(e.target.value)}
              value={filterApm}
            >
              <option value="">All agreements</option>
              {agreements.map((a) => (
                <option
                  key={a.id}
                  value={a.id}
                >
                  {`${a.targetName ?? 'N/A'}${a.targetType === 'CONNECTOR' ? ' (connector)' : ''}`}
                </option>
              ))}
            </select>
          </div>

          <div className="ref-reporting-filter-group">
            <div className="ref-reporting-filter-lbl">Merchant</div>
            <select
              aria-label="Filter by Merchant"
              className="ref-reporting-select"
              onChange={(e) => setFilterMerchant(e.target.value)}
              value={filterMerchant}
            >
              <option value="">All Merchants</option>
              {merchants.map((m) => (
                <option
                  key={m.id}
                  value={m.id}
                >
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ref-reporting-filter-group">
            <div className="ref-reporting-filter-lbl">Parent</div>
            <select
              aria-label="Filter by Parent"
              className="ref-reporting-select"
              onChange={(e) => setFilterParent(e.target.value)}
              value={filterParent}
            >
              <option value="">All Parents</option>
              {parents.map((p) => (
                <option
                  key={p.id}
                  value={p.id}
                >
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ref-reporting-filter-group">
            <div className="ref-reporting-filter-lbl">Grandparent</div>
            <select
              aria-label="Filter by Grandparent"
              className="ref-reporting-select"
              onChange={(e) => setFilterGp(e.target.value)}
              value={filterGp}
            >
              <option value="">All Grandparents</option>
              {grandparents.map((g) => (
                <option
                  key={g.id}
                  value={g.id}
                >
                  {g.name}
                </option>
              ))}
            </select>
          </div>

        </div>

        <div className="ref-reporting-date-row">
          <div className="ref-reporting-date-group">
            <div className="ref-reporting-filter-lbl">Date From</div>
            <input
              aria-label="Date from"
              className="ref-reporting-date-input"
              onChange={(e) => setDateFrom(e.target.value)}
              type="date"
              value={dateFrom}
            />
          </div>
          <div className="ref-reporting-date-group">
            <div className="ref-reporting-filter-lbl">Date To</div>
            <input
              aria-label="Date to"
              className="ref-reporting-date-input"
              onChange={(e) => setDateTo(e.target.value)}
              type="date"
              value={dateTo}
            />
          </div>
          <div className="ref-reporting-date-hint">
            Input the exact period from a provider-submitted invoice to cross-reference transaction data
          </div>
          <button
            className="btn btn-primary ref-reporting-search-btn"
            disabled={loading}
            onClick={() => fetchData()}
            type="button"
          >
            {loading ? 'Loading…' : 'Search'}
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="ref-reporting-stats">
        <div className="ref-reporting-stat-card">
          <div className="ref-reporting-stat-label">TOTAL VOLUME</div>
          {loading ? (
            <>
              <span
                className="skel"
                style={{
                  display: 'block', height: 32, margin: '4px 0', width: 160,
                }}
              />
              <span
                className="skel"
                style={{ height: 14, width: 100 }}
              />
            </>
          ) : (
            <>
              <div className="ref-reporting-stat-value">
                {summary ? fmtCurrency(summary.totalVolume, displayCurrency) : '—'}
              </div>
              <div className="ref-reporting-stat-sub">
                {summary ? `${summary.rowCount.toLocaleString()} transactions` : 'No data yet'}
              </div>
            </>
          )}
        </div>

        <div className="ref-reporting-stat-card">
          <div className="ref-reporting-stat-label">EST. REFERRAL FEE</div>
          {loading ? (
            <>
              <span
                className="skel"
                style={{
                  display: 'block', height: 32, margin: '4px 0', width: 140,
                }}
              />
              <span
                className="skel"
                style={{ height: 14, width: 110 }}
              />
            </>
          ) : (
            <>
              <div className="ref-reporting-stat-value">
                {summary ? fmtCurrency(summary.estReferralFee, displayCurrency) : '—'}
              </div>
              <div className="ref-reporting-stat-sub">Across filtered rows</div>
            </>
          )}
        </div>

        <div className="ref-reporting-stat-card">
          <div className="ref-reporting-stat-label">UNIQUE MERCHANTS</div>
          {loading ? (
            <>
              <span
                className="skel"
                style={{
                  display: 'block', height: 32, margin: '4px 0', width: 60,
                }}
              />
              <span
                className="skel"
                style={{ height: 14, width: 120 }}
              />
            </>
          ) : (
            <>
              <div className="ref-reporting-stat-value">
                {summary ? summary.uniqueMerchants : '—'}
              </div>
              <div className="ref-reporting-stat-sub">Transacted in selection</div>
            </>
          )}
        </div>

        <div className="ref-reporting-stat-card">
          <div className="ref-reporting-stat-label">AVG. REFERRAL RATE</div>
          {loading ? (
            <>
              <span
                className="skel"
                style={{
                  display: 'block', height: 32, margin: '4px 0', width: 100,
                }}
              />
              <span
                className="skel"
                style={{ height: 14, width: 90 }}
              />
            </>
          ) : (
            <>
              <div className="ref-reporting-stat-value">
                {summary ? `${summary.avgBips.toFixed(1)} bps` : '—'}
              </div>
              <div className="ref-reporting-stat-sub">Weighted average</div>
            </>
          )}
        </div>
      </div>

      {/* Transactions table */}
      <div className="invoicing-form-child ref-rpt-table-card">
        <div className="ref-rpt-head">
          <div>Date</div>
          <div>Brand</div>
          <div>Merchant</div>
          <div>Parent</div>
          <div>Grandparent</div>
          <div className="ref-rpt-right">Tx Amount</div>
          <div className="ref-rpt-right">Inv. Amount</div>
          <div className="ref-rpt-right">BiPs</div>
          <div className="ref-rpt-right">Ref. Fee</div>
        </div>

        {loading && SKEL_ROWS.map((id) => (
          <div
            className="ref-rpt-row"
            key={`skel-${id}`}
          >
            <div>
              <span
                className="skel"
                style={{ height: 13, width: 70 }}
              />
            </div>
            <div>
              <span
                className="skel skel--pill"
                style={{ height: 22, width: 80 }}
              />
            </div>
            <div>
              <span
                className="skel"
                style={{ height: 13, width: 130 }}
              />
            </div>
            <div>
              <span
                className="skel"
                style={{ height: 13, width: 100 }}
              />
            </div>
            <div>
              <span
                className="skel"
                style={{ height: 13, width: 90 }}
              />
            </div>
            <div className="ref-rpt-right">
              <span
                className="skel"
                style={{ height: 13, width: 80 }}
              />
            </div>
            <div className="ref-rpt-right">
              <span
                className="skel"
                style={{ height: 13, width: 80 }}
              />
            </div>
            <div className="ref-rpt-right">
              <span
                className="skel"
                style={{ height: 13, width: 30 }}
              />
            </div>
            <div className="ref-rpt-right">
              <span
                className="skel"
                style={{ height: 13, width: 60 }}
              />
            </div>
          </div>
        ))}

        {!loading && rows.length === 0 && (
          <div className="ref-table-empty">
            {result === null
              ? 'Select filters and click Search to load referral transaction data.'
              : 'No transactions found for the selected filters.'}
          </div>
        )}

        {!loading && rows.map((row) => (
          <div
            className="ref-rpt-row"
            key={`${row.date}-${row.merchantId}-${row.agreementId}-${row.paymentBrandName}`}
          >
            <div className="ref-rpt-date">{row.date}</div>
            <div>
              <span
                className="ref-apm-pill"
                style={{
                  background: `${brandColor(row.paymentBrandName)}18`,
                  border: `1px solid ${brandColor(row.paymentBrandName)}40`,
                  color: brandColor(row.paymentBrandName),
                }}
                title={`${row.agreementTargetType === 'CONNECTOR' ? 'Connector' : 'Brand'} agreement: ${row.agreementName}`}
              >
                {row.paymentBrandName}
              </span>
            </div>
            <div className="ref-rpt-merchant">{row.merchantName}</div>
            <div className="ref-rpt-sub">{row.parentName ?? '—'}</div>
            <div className="ref-rpt-sub">{row.grandparentName ?? '—'}</div>
            <div className="ref-rpt-right ref-rpt-amount">
              {fmtCurrency(row.amount + row.surcharge, row.transactionCurrency)}
            </div>
            <div className="ref-rpt-right ref-rpt-amount">
              {fmtCurrency(row.amountInInvoicingCurrency, row.invoicingCurrency)}
            </div>
            <div className="ref-rpt-right ref-rpt-bips">{row.bips}</div>
            <div className="ref-rpt-right ref-rpt-fee">
              {fmtCurrency(row.referralFee, row.invoicingCurrency)}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default ReferralReportingPage;
